import type { Phrases } from '@/schemas';

/**
 * A phrase left at its default is equivalent to an unset one — the caregiver
 * hasn't customised it, so it must not read as an unsaved change.
 *
 * The Voice page pre-fills each phrase input with the resolved default *text*
 * (rather than an empty field showing the default as a placeholder), so any
 * keystroke — even a net-zero edit that lands back on the default — writes the
 * default string into the draft. Comparing that raw draft against the account
 * doc, where an uncustomised phrase is simply *absent*, then reports a spurious
 * "You have unsaved changes" that the caregiver can't clear (the value looks
 * default, so the "Reset to default" affordance is hidden) and that survives a
 * reload (drafts are sessionStorage-backed).
 *
 * `stripDefaultPhrases` collapses that difference: it drops any key whose value
 * equals the field default, so an at-default phrase compares equal to an unset
 * one — both on the dirty check and on save (so we never persist a redundant
 * default phrase). An empty string is a deliberate "silent" customisation
 * (distinct from the default text) and is kept. Keys are emitted in sorted
 * order so the result serialises canonically regardless of the source object's
 * key order (a Firestore map vs a sessionStorage-restored draft), removing a
 * latent key-order source of spurious diffs.
 */
export function stripDefaultPhrases(
  phrases: Phrases,
  defaults: Record<string, string>,
): Phrases {
  const out: Phrases = {};
  for (const key of (Object.keys(phrases) as (keyof Phrases)[]).sort()) {
    const value = phrases[key];
    if (value === undefined) continue; // never customised
    if (value === defaults[key]) continue; // at default ⇒ same as unset
    out[key] = value;
  }
  return out;
}

/**
 * The per-key write set for a phrase edit: only the keys the caregiver
 * actually touched, each normalised through the at-default rule above —
 * an at-default (or never-set) value maps to `null`, meaning "delete the
 * key from the doc" (updateAccountPhrases turns it into deleteField()).
 *
 * Per-key (dot-path) writes are what make phrase saves field-granular
 * like every other control: two caregivers editing DIFFERENT phrases at
 * the same time can no longer overwrite each other, which a whole-map
 * write could.
 */
export function phrasePatchFromEdits(
  phrases: Phrases,
  editedKeys: ReadonlyArray<keyof Phrases>,
  defaults: Record<string, string>,
): Partial<Record<keyof Phrases, string | null>> {
  const out: Partial<Record<keyof Phrases, string | null>> = {};
  for (const key of [...new Set(editedKeys)].sort()) {
    const value = phrases[key];
    out[key] = value === undefined || value === defaults[key] ? null : value;
  }
  return out;
}
